import { IDayCell } from './calendar'
import { hijriMonthLength } from './hijri'
import { jalaaliMonthLength } from './jalaali'

// The shape of a user's own occasion and the rule that decides which days it
// falls on. Pure: no storage and no baseline here, so the rule can be read —
// and tested — on its own. The store that holds these lives in
// services/custom-occasions.ts.

// Which calendar governs the yearly repeat; 'none' is a one-off on its own date.
export type Repeat = 'none' | 'jalaali' | 'gregorian' | 'hijri'
// How long before the day to notify. Collected by the wizard; delivery is separate.
export type Reminder = 'none' | 'week' | 'day' | 'hour'

// The day it was made for is kept in all three calendars, so a repeat matches on
// month + day in whichever one the user picked — the same shape the built-in
// lists match on (see services/occasions.ts). Nothing is converted at read time,
// so a Jalaali anniversary stays on its Jalaali date however the grid is set.
export interface ICustomOccasion {
    id: string
    title: string
    repeat: Repeat
    reminder: Reminder
    jy: number
    jm: number
    jd: number
    gy: number
    gm: number
    gd: number
    hy: number
    hm: number
    hd: number
    // Set when this occasion was adopted from an event in the phone's calendar:
    // that event's id. The phone still holds the original, so the display leaves
    // it out — see services/phone-calendar.ts. Optional, so occasions saved
    // before adoption existed read back unchanged.
    source?: string
    // Insertion order. IndexedDB returns key order and shortUUID ids are
    // random, so add() stamps this and init() sorts by it. Optional so rows
    // saved before it existed keep the engine's load order.
    seq?: number
}

export type OccasionDate = Pick<ICustomOccasion, 'jy' | 'jm' | 'jd' | 'gy' | 'gm' | 'gd' | 'hy' | 'hm' | 'hd'>

// A day past the month's length — a 29 February, or a hijri 30th in a 29-day
// month — falls on that month's last day, so an anniversary never skips a year
// in silence.
const sameDay = (day: number, cellDay: number, length: number) =>
    cellDay === Math.min(day, length)

export function repeatsOn(o: ICustomOccasion, cell: IDayCell): boolean {
    if (o.repeat === 'jalaali') {
        return o.jm === cell.jm && sameDay(o.jd, cell.jd, jalaaliMonthLength(cell.jy, cell.jm))
    }
    if (o.repeat === 'gregorian') {
        return o.gm === cell.gm && sameDay(o.gd, cell.gd, new Date(cell.gy, cell.gm, 0).getDate())
    }
    if (o.repeat === 'hijri') {
        return o.hm === cell.hm && sameDay(o.hd, cell.hd, hijriMonthLength(cell.hy, cell.hm))
    }
    return o.gy === cell.gy && o.gm === cell.gm && o.gd === cell.gd
}
